import React, { useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Avatar,
  Switch,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  Security,
  ExpandMore,
  Refresh,
  ContentCopy,
  Info,
} from "@mui/icons-material";

const Roles = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  // Get institute context - if Super Admin is managing a specific institute
  const { selectedInstitute, isInInstituteContext } = useSelector(
    (state) => state.instituteContext
  );

  const roles = [
    {
      id: 1,
      name: "Student",
      users: 1500,
      description: "Basic student access",
      permissions: ["view_materials", "submit_assignments", "view_attendance"],
      isSystem: true,
      status: "active",
    },
    {
      id: 2,
      name: "Teacher",
      users: 50,
      description: "Teacher with class management",
      permissions: ["manage_attendance", "manage_assignments", "view_students"],
      isSystem: true,
      status: "active",
    },
    {
      id: 3,
      name: "HOD",
      users: 8,
      description: "Department head with department access",
      permissions: ["manage_department", "manage_teachers", "approve_leaves"],
      isSystem: true,
      status: "active",
    },
    {
      id: 4,
      name: "Admin",
      users: 5,
      description: "Administrator with full institute access",
      permissions: ["manage_users", "manage_departments", "manage_roles"],
      isSystem: true,
      status: "active",
    },
    {
      id: 5,
      name: "Custom Role",
      users: 10,
      description: "Custom role with specific permissions",
      permissions: ["view_reports", "manage_attendance"],
      isSystem: false,
      status: "active",
    },
  ];

  const permissionModules = [
    {
      name: "Users",
      permissions: [
        "view_users",
        "create_users",
        "edit_users",
        "delete_users",
        "assign_roles",
        "import_users",
      ],
    },
    {
      name: "Departments",
      permissions: [
        "view_departments",
        "create_departments",
        "edit_departments",
        "delete_departments",
      ],
    },
    {
      name: "Attendance",
      permissions: [
        "view_attendance",
        "manage_attendance",
        "export_attendance",
        "generate_qr_otp",
      ],
    },
    {
      name: "Assignments",
      permissions: [
        "view_assignments",
        "create_assignments",
        "edit_assignments",
        "delete_assignments",
        "grade_assignments",
      ],
    },
    {
      name: "Leave Management",
      permissions: [
        "view_leaves",
        "apply_leave",
        "approve_leaves",
        "manage_leave_types",
        "manage_leave_allocations",
        "view_leave_reports",
      ],
    },
    {
      name: "Timetable",
      permissions: [
        "view_timetable",
        "create_timetable",
        "edit_timetable",
        "request_schedule_change",
        "approve_schedule_change",
      ],
    },
    {
      name: "Exams",
      permissions: [
        "view_exams",
        "create_exams",
        "edit_exams",
        "mark_scores",
        "publish_results",
      ],
    },
    {
      name: "Materials",
      permissions: [
        "view_materials",
        "upload_materials",
        "edit_materials",
        "delete_materials",
      ],
    },
    {
      name: "Notifications",
      permissions: [
        "view_notifications",
        "create_class_notification",
        "create_department_notification",
        "create_institute_notification",
      ],
    },
    {
      name: "Chat & Communication",
      permissions: [
        "view_chat",
        "send_messages",
        "create_group_chat",
        "create_broadcast",
      ],
    },
    {
      name: "Courses & Subjects",
      permissions: [
        "view_courses",
        "create_courses",
        "edit_courses",
        "view_subjects",
        "create_subjects",
        "edit_subjects",
      ],
    },
    {
      name: "Reports & Analytics",
      permissions: [
        "view_reports",
        "export_reports",
        "view_analytics",
        "download_reports",
      ],
    },
    {
      name: "System Settings",
      permissions: [
        "view_settings",
        "edit_settings",
        "manage_roles",
        "manage_rbac",
      ],
    },
  ];

  const columns = [
    {
      field: "name",
      headerName: "Role",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "secondary.light", width: 40, height: 40 }}>
            <Security />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.displayName || params.row.name}
              {params.row.isSystem && (
                <Chip
                  label="System"
                  size="small"
                  sx={{ ml: 1, fontSize: 10 }}
                />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.description}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "users", headerName: "Users", width: 100 },
    {
      field: "permissions",
      headerName: "Permissions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          {params.row.permissions.slice(0, 2).map((p, i) => (
            <Chip
              key={i}
              label={p.replace("_", " ")}
              size="small"
              sx={{ mr: 0.5, textTransform: "capitalize" }}
            />
          ))}
          {params.row.permissions.length > 2 && (
            <Chip
              label={`+${params.row.permissions.length - 2}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Switch checked={params.value === "active"} size="small" />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton size="small">
            <Edit />
          </IconButton>
          {!params.row.isSystem && (
            <IconButton size="small" color="error">
              <Delete />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Institute Context Banner */}
      {isInInstituteContext && selectedInstitute && (
        <Alert
          severity="info"
          icon={<Info />}
          sx={{ mb: 3 }}
          action={
            <Tooltip title="Reset roles to platform defaults">
              <Button
                color="inherit"
                size="small"
                startIcon={<Refresh />}
                onClick={() => setResetDialogOpen(true)}
              >
                Reset to Defaults
              </Button>
            </Tooltip>
          }
        >
          <Typography variant="body2">
            <strong>Managing roles for: {selectedInstitute.name}</strong>
            <br />
            You can customize roles and permissions specifically for this
            institute. Changes here won't affect other institutes.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {isInInstituteContext
              ? `Role Management - ${selectedInstitute?.name || "Institute"}`
              : "Role Management"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isInInstituteContext
              ? "Create, update and manage roles & permissions for this institute"
              : "Manage roles and permissions"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isInInstituteContext && (
            <Tooltip title="Clone from another role">
              <Button variant="outlined" startIcon={<ContentCopy />}>
                Clone Role
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Role
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: "Total Roles", value: roles.length },
          {
            label: "System Roles",
            value: roles.filter((r) => r.isSystem).length,
          },
          {
            label: "Custom Roles",
            value: roles.filter((r) => !r.isSystem).length,
          },
          {
            label: "Total Users",
            value: roles.reduce((a, r) => a + r.users, 0),
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search roles..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <DataGrid
            rows={roles}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setCreateDialogOpen(false); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Create New Role
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Permissions
              </Typography>
              {permissionModules.map((module, i) => (
                <Accordion key={i} defaultExpanded={i === 0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight={500}>{module.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container>
                      {module.permissions.map((perm, j) => (
                        <Grid item xs={6} sm={3} key={j}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label={
                              <Typography
                                variant="body2"
                                sx={{ textTransform: "capitalize" }}
                              >
                                {perm.replace(/_/g, " ")}
                              </Typography>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Role</Button>
        </DialogActions>
      </Dialog>

      {/* Reset to Defaults Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setResetDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Reset Roles to Platform Defaults
            <IconButton onClick={() => setResetDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will reset all roles and permissions to the platform
            default settings. Custom roles will be preserved but their
            permissions will be reset.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The following will happen:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2">
              System roles (Student, Teacher, HOD, Admin) will be reset to
              default permissions
            </Typography>
            <Typography component="li" variant="body2">
              Custom roles will retain their names but permissions will be
              cleared
            </Typography>
            <Typography component="li" variant="body2">
              User role assignments will remain unchanged
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Refresh />}
            onClick={() => {
              // Call rbacService.resetInstituteRolesToDefaults(instituteId)
              setResetDialogOpen(false);
            }}
          >
            Reset to Defaults
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles;
